/**
 * Scene 7: The Consistency Check
 * "Volatility in Revenue"
 */

import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PulseIcon, FactoryIcon, OilDerrickIcon } from '../../icons/StoryIcons';
import type { StoryInsights } from '../../../hooks/useStoryData';

gsap.registerPlugin(ScrollTrigger);

interface Scene7Props {
  insights: StoryInsights;
  isActive?: boolean;
}

export function Scene7Volatility({ insights, isActive }: Scene7Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const volatileRef = useRef<HTMLDivElement>(null);
  const stableRef = useRef<HTMLDivElement>(null);
  const pulseContainerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const ctx = gsap.context(() => {
      // Only set initial hidden state if not active
      if (!isActive) {
        gsap.set(titleRef.current, { opacity: 0, y: 50 });
        gsap.set(volatileRef.current, { opacity: 0, x: -50 });
        gsap.set(stableRef.current, { opacity: 0, x: 50 });
        gsap.set(pulseContainerRef.current, { opacity: 0 });
      }
      
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top center',
          end: 'center center',
          toggleActions: 'play none none reverse',
        },
      });
      
      tl.to(titleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      })
      .to(pulseContainerRef.current, {
        opacity: 1,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.4')
      .to(volatileRef.current, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.4')
      .to(stableRef.current, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.6');
      
      // Animate pulse lines
      if (pulseContainerRef.current) {
        const volatileLine = pulseContainerRef.current.querySelector('.volatile-line');
        const stableLine = pulseContainerRef.current.querySelector('.stable-line');
        
        if (volatileLine) {
          const length = (volatileLine as SVGPathElement).getTotalLength();
          gsap.set(volatileLine, { strokeDasharray: length });
          gsap.fromTo(volatileLine, 
            { strokeDashoffset: length },
            {
              strokeDashoffset: 0,
              duration: 2,
              ease: 'none',
              repeat: -1,
            }
          );
        }
        
        if (stableLine) {
          const length = (stableLine as SVGPathElement).getTotalLength();
          gsap.set(stableLine, { strokeDasharray: length });
          gsap.fromTo(stableLine,
            { strokeDashoffset: length },
            {
              strokeDashoffset: 0,
              duration: 3,
              ease: 'none',
              repeat: -1,
            }
          );
        }
      }
    }, containerRef);
    
    return () => ctx.revert();
  }, []);
  
  // Force visibility when scene becomes active (for auto-scroll)
  useEffect(() => {
    if (isActive) {
      gsap.to(titleRef.current, { opacity: 1, y: 0, duration: 0.5 });
      gsap.to(pulseContainerRef.current, { opacity: 1, duration: 0.5 });
      gsap.to(volatileRef.current, { opacity: 1, x: 0, duration: 0.5 });
      gsap.to(stableRef.current, { opacity: 1, x: 0, duration: 0.5 });
    }
  }, [isActive]);
  
  // Generate EKG-style paths
  const generateVolatilePath = () => {
    // Erratic heartbeat pattern
    return 'M0,50 L20,50 L25,20 L30,80 L35,10 L40,90 L45,30 L50,70 L55,50 L60,50 L65,15 L70,85 L75,25 L80,75 L85,40 L90,60 L95,50 L100,50 L105,30 L110,70 L115,20 L120,80 L125,50 L130,50 L135,5 L140,95 L145,50 L150,50';
  };
  
  const generateStablePath = () => {
    // Steady, gentle wave pattern
    return 'M0,50 L20,50 L25,45 L30,55 L35,48 L40,52 L45,50 L60,50 L65,46 L70,54 L75,49 L80,51 L85,50 L100,50 L105,47 L110,53 L115,48 L120,52 L125,50 L140,50 L145,46 L150,50';
  };
  
  return (
    <section
      ref={containerRef}
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-4 py-20"
    >
      {/* Background heartbeat pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <svg className="w-full h-full" preserveAspectRatio="none">
          <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <line x1="50" y1="0" x2="50" y2="50" stroke="#06b6d4" strokeWidth="0.5" />
            <line x1="0" y1="50" x2="50" y2="50" stroke="#06b6d4" strokeWidth="0.5" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-5xl w-full">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-12 group">
          <div className="flex items-center justify-center gap-4 mb-4">
            <PulseIcon size={40} className="text-accent-400 transition-all duration-500 group-hover:scale-110 group-hover:drop-shadow-[0_0_12px_rgba(6,182,212,0.5)]" />
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white transition-all duration-300 group-hover:tracking-wide">
              The Consistency Check
            </h2>
          </div>
          <p className="text-xl md:text-2xl text-surface-300">
            Some states have <span className="text-danger-400 transition-all duration-300 hover:text-danger-300 hover:drop-shadow-[0_0_8px_rgba(239,68,68,0.5)] cursor-default">volatile</span> allocations, 
            others are <span className="text-success-400 transition-all duration-300 hover:text-success-300 hover:drop-shadow-[0_0_8px_rgba(34,197,94,0.5)] cursor-default">stable</span>
          </p>
        </div>
        
        {/* Pulse visualization */}
        <div ref={pulseContainerRef} className="mb-12 p-8 bg-surface-800/50 backdrop-blur-sm rounded-2xl border border-surface-700 transition-all duration-300 hover:bg-surface-800/70 hover:border-surface-600">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Volatile visualization */}
            <div className="group cursor-pointer">
              <div className="flex items-center gap-3 mb-4">
                <OilDerrickIcon size={24} className="text-danger-400 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3" />
                <span className="text-danger-400 font-semibold transition-all duration-300 group-hover:text-danger-300">Oil States (Volatile)</span>
              </div>
              <div className="bg-surface-900 rounded-xl p-4 h-24 flex items-center transition-all duration-300 group-hover:bg-surface-900/80 group-hover:shadow-lg group-hover:shadow-danger-500/20 group-hover:ring-1 group-hover:ring-danger-500/30">
                <svg viewBox="0 0 150 100" className="w-full h-full transition-transform duration-300 group-hover:scale-105">
                  <path
                    className="volatile-line"
                    d={generateVolatilePath()}
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <p className="mt-2 text-sm text-surface-400 text-center transition-colors duration-300 group-hover:text-surface-300">
                Subject to oil price fluctuations
              </p>
            </div>
            
            {/* Stable visualization */}
            <div className="group cursor-pointer">
              <div className="flex items-center gap-3 mb-4">
                <FactoryIcon size={24} className="text-success-400 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3" />
                <span className="text-success-400 font-semibold transition-all duration-300 group-hover:text-success-300">Non-Oil States (Stable)</span>
              </div>
              <div className="bg-surface-900 rounded-xl p-4 h-24 flex items-center transition-all duration-300 group-hover:bg-surface-900/80 group-hover:shadow-lg group-hover:shadow-success-500/20 group-hover:ring-1 group-hover:ring-success-500/30">
                <svg viewBox="0 0 150 100" className="w-full h-full transition-transform duration-300 group-hover:scale-105">
                  <path
                    className="stable-line"
                    d={generateStablePath()}
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <p className="mt-2 text-sm text-surface-400 text-center transition-colors duration-300 group-hover:text-surface-300">
                Consistent, predictable allocations
              </p>
            </div>
          </div>
        </div>
        
        {/* Top volatile vs stable states */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Most Volatile */}
          <div ref={volatileRef}>
            <div className="flex items-center gap-3 mb-4 group cursor-default">
              <div className="p-2 bg-danger-500/20 rounded-lg transition-all duration-300 group-hover:bg-danger-500/30 group-hover:scale-110">
                <PulseIcon size={20} className="text-danger-400 transition-transform duration-300 group-hover:animate-pulse" />
              </div>
              <h3 className="text-xl font-semibold text-white transition-colors duration-300 group-hover:text-danger-300">Most Volatile States</h3>
            </div>
            
            <div className="space-y-3">
              {insights.mostVolatile.map((state, index) => {
                const isOilState = insights.volatilityRankings.find(
                  v => v.state === state.state
                )?.isOilState;
                
                return (
                  <div
                    key={state.state}
                    className="group/card p-4 bg-danger-500/10 rounded-xl border border-danger-500/30 flex items-center gap-4 cursor-pointer transition-all duration-300 hover:bg-danger-500/20 hover:border-danger-500/50 hover:shadow-lg hover:shadow-danger-500/10 hover:-translate-y-1 hover:scale-[1.02]"
                  >
                    <div className="w-8 h-8 rounded-full bg-danger-500 text-white flex items-center justify-center font-bold text-sm transition-all duration-300 group-hover/card:scale-110 group-hover/card:shadow-md group-hover/card:shadow-danger-500/50">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white transition-colors duration-300 group-hover/card:text-danger-200">{state.state}</span>
                        {isOilState && (
                          <OilDerrickIcon size={14} className="text-amber-400 transition-transform duration-300 group-hover/card:scale-125 group-hover/card:rotate-12" />
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-danger-400 transition-all duration-300 group-hover/card:text-danger-300 group-hover/card:scale-110">
                        {state.volatility.toFixed(1)}%
                      </div>
                      <div className="text-xs text-surface-500 transition-colors duration-300 group-hover/card:text-surface-400">volatility</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Most Stable */}
          <div ref={stableRef}>
            <div className="flex items-center gap-3 mb-4 group cursor-default">
              <div className="p-2 bg-success-500/20 rounded-lg transition-all duration-300 group-hover:bg-success-500/30 group-hover:scale-110">
                <FactoryIcon size={20} className="text-success-400 transition-transform duration-300 group-hover:scale-110" />
              </div>
              <h3 className="text-xl font-semibold text-white transition-colors duration-300 group-hover:text-success-300">Most Stable States</h3>
            </div>
            
            <div className="space-y-3">
              {insights.mostStable.map((state, index) => {
                const isOilState = insights.volatilityRankings.find(
                  v => v.state === state.state
                )?.isOilState;
                
                return (
                  <div
                    key={state.state}
                    className="group/card p-4 bg-success-500/10 rounded-xl border border-success-500/30 flex items-center gap-4 cursor-pointer transition-all duration-300 hover:bg-success-500/20 hover:border-success-500/50 hover:shadow-lg hover:shadow-success-500/10 hover:-translate-y-1 hover:scale-[1.02]"
                  >
                    <div className="w-8 h-8 rounded-full bg-success-500 text-surface-900 flex items-center justify-center font-bold text-sm transition-all duration-300 group-hover/card:scale-110 group-hover/card:shadow-md group-hover/card:shadow-success-500/50">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white transition-colors duration-300 group-hover/card:text-success-200">{state.state}</span>
                        {isOilState && (
                          <OilDerrickIcon size={14} className="text-amber-400 transition-transform duration-300 group-hover/card:scale-125 group-hover/card:rotate-12" />
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-success-400 transition-all duration-300 group-hover/card:text-success-300 group-hover/card:scale-110">
                        {state.volatility.toFixed(1)}%
                      </div>
                      <div className="text-xs text-surface-500 transition-colors duration-300 group-hover/card:text-surface-400">volatility</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Insight */}
        <div className="mt-8 p-6 bg-surface-800/50 backdrop-blur-sm rounded-2xl border border-surface-700 transition-all duration-500 hover:bg-surface-800/70 hover:border-accent-500/30 hover:shadow-xl hover:shadow-accent-500/5 group cursor-default">
          <p className="text-surface-300 text-center transition-colors duration-300 group-hover:text-surface-200">
            <span className="text-white font-semibold transition-colors duration-300 group-hover:text-accent-300">Volatility</span> measures how much a state's 
            allocation fluctuates year-over-year. Oil-producing states tend to have higher volatility 
            because their allocations are tied to{' '}
            <span className="text-amber-400 font-semibold transition-all duration-300 group-hover:text-amber-300 group-hover:drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]">crude oil prices</span>, which can swing 
            dramatically based on global market conditions.
          </p>
        </div>
        
        {/* Oil state indicator */}
        <div className="mt-4 flex items-center justify-center gap-2 text-sm text-surface-400 group cursor-pointer transition-all duration-300 hover:text-surface-300">
          <OilDerrickIcon size={14} className="text-amber-400 transition-all duration-300 group-hover:scale-125 group-hover:rotate-6 group-hover:drop-shadow-[0_0_6px_rgba(251,191,36,0.6)]" />
          <span className="transition-colors duration-300">= Oil-producing state</span>
        </div>
      </div>
    </section>
  );
}

export default Scene7Volatility;

